import { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import dayjs from 'dayjs'
import {
    fetchPlantOpData,
    Deletenewplant
} from '../../../services/api'
import { fetchIssues } from '../../IssueManagement/services/issueService'

export const usePlantTechnicalView = () => {
    const { state } = useLocation()
    const navigate = useNavigate()

    const plantData = state?.data
    const token = state?.token || localStorage.getItem('accessToken')

    const [plantContent] = useState(plantData || {})
    const [liveData, setLiveData] = useState({
        dailyEnergyMFM: '—',
        inverterEToday: '—',
        gtiValue: '—',
        specificYield: '—',
        co2Avoided: '—',
    })
    const [hourGlassValue, setHourGlassValue] = useState(0)
    const [notifications, setNotifications] = useState([])
    const [notificationCount, setNotificationCount] = useState(0)
    const [loading, setLoading] = useState(false)
    const [issueCount, setIssueCount] = useState(0)

    // Internal state for dialogs and tabs
    const [page, setPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [showNotifications, setShowNotifications] = useState(false)
    const [selectedNotification, setSelectedNotification] = useState(null)
    const [activeTab, setActiveTab] = useState('unread')
    const [liveTabActive, setLiveTabActive] = useState('graphs')
    const [open, setOpen] = useState(false)
    const [dleted, setdelete] = useState(false)

    const fetchLivePlantData = useCallback(async () => {
        if (!plantContent?.id || !token) return
        try {
            const date = state?.selectedDate
                ? dayjs(state.selectedDate, 'DD-MM-YYYY').format('YYYY-MM-DD')
                : dayjs().format('YYYY-MM-DD')

            const response = await fetchPlantOpData(date, token, 'asc', plantContent.id)

            if (response?.data?.data?.[0]) {
                const apiData = response.data.data[0]
                setLiveData({
                    dailyEnergyMFM: apiData.totalEToday ? parseFloat(apiData.totalEToday).toFixed(2) : '0.00',
                    inverterEToday: apiData.dailyEnergy ? parseFloat(apiData.dailyEnergy).toFixed(2) : '0.00',
                    gtiValue: apiData.gti ? parseFloat(apiData.gti).toFixed(2) : '0.00',
                    specificYield: apiData.yield ? parseFloat(apiData.yield).toFixed(2) : '0.00',
                    co2Avoided: apiData.Co2_avoided ? parseFloat(apiData.Co2_avoided).toFixed(2) : '0.00',
                })
            }
        } catch (error) {
            console.error('Error fetching live plant data:', error)
        }
    }, [plantContent?.id, token, state?.selectedDate])

    const fetchNotifications = useCallback(async () => {
        if (!plantContent?.plantName || !token) return
        setLoading(true)
        try {
            const statusFilter = activeTab === 'unread' ? 'unread' : 'read'
            const response = await axios.get(
                `${process.env.REACT_APP_V4_API}/monitoring/notifications`,
                {
                    params: {
                        plantName: plantContent.plantName,
                        status: statusFilter,
                        page,
                        limit: 10,
                    },
                    headers: { Authorization: `Bearer ${token}` },
                }
            )
            if (response.data?.success) {
                setNotifications(response.data.data.notifications)
                setTotalPages(response.data.data.totalPages)
                if (activeTab === 'unread') {
                    setNotificationCount(response.data.data.totalCount)
                }
            }
        } catch (error) {
            console.error('Error fetching notifications:', error)
        } finally {
            setLoading(false)
        }
    }, [plantContent?.plantName, token, activeTab, page])

    const markNotificationAsRead = async (notification) => {
        try {
            await axios.patch(
                `${process.env.REACT_APP_V4_API}/monitoring/notifications/${notification.id}/status`,
                { status: 'read' },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            setSelectedNotification(notification)
            fetchNotifications()
        } catch (error) {
            console.error('Error marking notification as read:', error)
        }
    }

    const markNotificationAsDone = async (notification, e) => {
        e?.stopPropagation()
        try {
            await axios.patch(
                `${process.env.REACT_APP_V4_API}/monitoring/notifications/${notification.id}/done`,
                { isDone: true },
                { headers: { Authorization: `Bearer ${token}` } }
            )
            fetchNotifications()
        } catch (error) {
            console.error('Error marking notification as done:', error)
        }
    }

    const markCurrentNotificationAsDone = async () => {
        if (selectedNotification) {
            try {
                await axios.patch(
                    `${process.env.REACT_APP_V4_API}/monitoring/notifications/${selectedNotification.id}/done`,
                    { isDone: true },
                    { headers: { Authorization: `Bearer ${token}` } }
                )
                setSelectedNotification({ ...selectedNotification, isDone: true })
                fetchNotifications()
            } catch (error) {
                console.error('Error marking current notification as done:', error)
            }
        }
    }

    const loadPlantIssues = useCallback(async () => {
        if (!plantContent?.id || !token) return
        try {
            const response = await fetchIssues(token, { plantId: plantContent.id })
            if (response?.success) {
                setIssueCount(response.data.length)
            }
        } catch (error) {
            console.error('Error loading plant issues:', error)
        }
    }, [plantContent?.id, token])

    const handleRemainingDate = useCallback(() => {
        const dateString = plantContent?.commissionDate || plantContent?.cod
        if (dateString) {
            const codDate = new Date(dateString)
            const totalDays = 25 * 365
            const currentDate = new Date()
            const timeDiff = Math.abs(currentDate.getTime() - codDate.getTime())
            const diffDays = Math.ceil(timeDiff / (1000 * 3600 * 24))
            const percentage = (diffDays / totalDays) * 100
            setHourGlassValue(percentage > 100 ? 100 : percentage)
        }
    }, [plantContent])

    useEffect(() => {
        if (plantContent?.id && token) {
            // Initialize with data from plantContent (passed via state)
            setLiveData({
                dailyEnergyMFM: plantContent.totalEToday ? parseFloat(plantContent.totalEToday).toFixed(2) : '0.00',
                inverterEToday: plantContent.dailyEnergy ? parseFloat(plantContent.dailyEnergy).toFixed(2) : '0.00',
                gtiValue: plantContent.gti ? parseFloat(plantContent.gti).toFixed(2) : '0.00',
                specificYield: plantContent.yield ? parseFloat(plantContent.yield).toFixed(2) : '0.00',
                co2Avoided: plantContent.Co2_avoided ? parseFloat(plantContent.Co2_avoided).toFixed(2) : '0.00',
            })

            // Fetch fresh data
            fetchLivePlantData()
            fetchNotifications()
            handleRemainingDate()
            loadPlantIssues()

            // Set up 5-minute interval for updates
            const interval = setInterval(() => {
                fetchLivePlantData()
                fetchNotifications()
            }, 300000)

            return () => clearInterval(interval)
        }
    }, [
        plantContent,
        token,
        fetchLivePlantData,
        fetchNotifications,
        handleRemainingDate,
        loadPlantIssues,
    ])

    // Navigation handlers
    const handleBusinessView = () => {
        navigate('/BusinessView', { state: { data: plantContent, token } })
    }

    const handlePlantMapView = () => {
        navigate('/PlantMapView', { state: { data: plantContent, token } })
    }

    const handleIssueManagement = () => {
        navigate(`/issue-management?plantId=${plantContent.id}&plantName=${encodeURIComponent(plantContent.plantName)}`)
    }


    const handleOpenEdit = () => {
        navigate('/addnewplant', { state: { plantData: plantContent, Edit: true, token: token } })
    }

    const handleDeleteplant = async (id) => {
        try {
            const response = await Deletenewplant(id, token)
            if (response.status === 200) {
                setdelete(false)
                navigate('/solarplantlist')
            }
        } catch (error) {
            console.error('Error deleting plant:', error)
        }
    }

    return {
        plantContent,
        liveData,
        hourGlassValue,
        notifications,
        notificationCount,
        loading,
        page,
        setPage,
        totalPages,
        showNotifications,
        setShowNotifications,
        selectedNotification,
        setSelectedNotification,
        activeTab,
        setActiveTab,
        issueCount,
        liveTabActive,
        setLiveTabActive,
        open,
        setOpen,
        dleted,
        setdelete,
        markNotificationAsRead,
        markNotificationAsDone,
        markCurrentNotificationAsDone,
        handleBusinessView,
        handlePlantMapView,
        handleIssueManagement,
        handleOpenEdit,
        handleDeleteplant,
        state,
        token,
    }
}
